import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ConstructionPlan from './subcontract/ConstructionPlan';
import PlanNodeProgress from './subcontract/PlanNodeProgress';
import { authStore } from "../lib/api";
import { useApp } from "../context/AppContext";
import { errTextOf } from "../lib/detailText";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "./ui/sonner";
import { Button } from "./ui/button";
import { chunkUpload } from "./EnginePanel";
import { EngineOpsPanel } from "./EngineOpsPanel";
import { CompleteNoteModal } from "./CompleteNoteModal";
import { listEvidence, queueEvidence, removeEvidence } from "../lib/offlineQueue";
import { EvidenceLightbox, evidenceUrl } from "./EvidenceLightbox";
import { BurstCamera } from "./BurstCamera";
import { Guide811 } from "./Guide811";
import { RfqCard } from "./RfqCard";
import { TaskSubCard } from "./engine/TaskSubCard";
import { TradeScopeDialog } from "./subcontract/SubTaskDialogs";
import { DraftCommPanel } from "./trace/DraftCommPanel";
import { VrPanel } from "./trace/VrPanel";
import { SurveyPanel } from "./trace/SurveyPanel";
import { LandSurveyPanel } from "./trace/LandSurveyPanel";
import { SiteOrderPanel, SiteRemovalPanel } from "./trace/SiteOrderPanel";
// 【2026-10-29】框架阶段两张补录任务卡的面板：厨房橱柜三步提交、衣柜三联框测量。
import { CabinetFlowPanel } from "./trace/CabinetFlowPanel";
import { ClosetOpeningsPanel } from "./trace/ClosetOpeningsPanel";
import {
  AlertTriangle, CalendarRange, Camera, CheckCircle2, FileDown, FileText, GitBranch,
  Loader2, MessageSquare, Mic, Plus,
  RefreshCw, RotateCcw, Share2, Square, Unlock, Upload, WifiOff, X, XCircle, ChevronDown, ChevronRight,
  Maximize2, Minimize2,
} from "lucide-react";
import { fmtD, fmtDT } from "../lib/date";
import { PhaseCalibrate } from "./PhaseCalibrate";
import { TracePhaseBar } from "./trace/TracePhaseBar";
import ProjectPausePanel, { ProjectPauseButton, useProjectPause } from "./trace/ProjectPausePanel";

import { T } from "../lib/i18n";
import { ink } from "../lib/ink";
import { AssigneeOptions, CoTag } from "./engine/AssigneeOptions";

const MANAGE = ["admin", "ceo", "eng_manager", "pm"];
const RELEASE_ROLES = ["admin", "ceo", "eng_manager"];
const STATE_BADGE = {
  ready: ["当前", "Current", "bg-brand-500 text-white"],
  done: ["完成 ✓", "Done ✓", "bg-emerald-600/90 text-white"],
  skipped: ["跳过", "Skipped", "bg-slate-300 text-slate-600"],
  blocked: ["阻塞", "Blocked", "bg-red-100 text-red-700 border border-red-300"],
  waiting_time: ["待时间", "Time", "bg-amber-400 text-slate-900"],
  waiting_external: ["等外部", "External", "bg-blue-600 text-white"],
  pending: ["排队", "Queued", "bg-slate-200 text-slate-500"],
  locked: ["锁定", "Locked", "bg-slate-100 text-slate-400"],
};
const ACTION_STEPS = [
  ["evidence", ["上传证据", "Evidence"], [["点「上传证据」", "一次可多选多张照片/PDF/文件", "逐张显示上传进度（失败可单张重试，无网络自动离线缓存）", "自动加水印留档，证据计数累加；误传可由管理层点缩略图角上 × 删除并记日志"], ["Click Evidence", "Pick multiple files", "Per-file progress (retry / offline cache)", "Watermark + count; managers can remove via ×"]]],
  ["camera", ["拍照上传", "Camera"], [["点「拍照上传（连拍）」打开取景器", "按白色快门连续拍多张；点缩略图可在照片上圈选/箭头/画笔/文字并写备注", "点「提交 N 张」一次性批量上传（标注自动合成到照片），逐张显示进度", "相机不可用时可改用系统相机；无网络自动离线缓存并补传"], ["Open burst camera", "Shoot many; tap a shot to annotate", "Submit N → batch upload with progress", "Fallback to system camera / offline cache"]]],
  ["reopen", ["重开", "Reopen"], [["管理层点「重开」", "填写重开原因（必填）", "已完成/跳过/等外部的任务回到待办，原因记入任务日志", "原完成人与当前责任人自动收到铃铛提醒"], ["Manager clicks Reopen", "Enter reason (required)", "Task returns to pending, reason logged", "Doer + assignee get a bell"]]],
  ["draft", ["起草联动消息", "Draft comm"], [["点「起草联动消息」", "自动套用模板并填入项目信息", "选择短信/邮件并修改文案", "存为草稿到通信中心 或 审核无误直接发送"], ["Click Draft", "Template auto-filled", "Pick SMS/Email, edit", "Save draft or send"]]],
  ["blocker", ["暂停项目", "Pause project"], [["点「暂停项目」", "填写原因并上传暂停依据、屋主通知信", "工程部经理批准后暂停 15 个自然日", "超过期限由 CEO 再次审批；复工由工程管理层确认"], ["Click Pause project", "Provide reason, evidence and homeowner notice", "Manager approval allows 15 calendar days", "CEO approves extensions; management resumes work"]]],
];
const CAT_DOT = {
  inspection: "bg-red-500", material: "bg-amber-500", comm: "bg-indigo-500", subtask: "bg-teal-500",
  owner: "bg-violet-500", precon: "bg-sky-500", cabinet: "bg-pink-500", closeout: "bg-emerald-500",
};
/* 责任角色半透明底色：一眼区分谁的活（pm 蓝 / 线上PM 紫 / 销售 琥珀 / 业主 玫红 / 分包 青 / 供应商 橙 / 市府 石板 / 工程队 青柠 / 系统 灰虚线） */
export const ROLE_TINT = {
  pm: { bg: "bg-blue-500/[0.18]", border: "border-blue-300", hover: "hover:bg-blue-500/25", pill: "bg-blue-600 text-white", dot: "bg-blue-500", bar: "border-l-blue-500" },
  cdc: { bg: "bg-violet-500/[0.18]", border: "border-violet-300", hover: "hover:bg-violet-500/25", pill: "bg-violet-600 text-white", dot: "bg-violet-500", bar: "border-l-violet-500" },
  sales: { bg: "bg-amber-500/[0.18]", border: "border-amber-300", hover: "hover:bg-amber-500/25", pill: "bg-amber-500 text-white", dot: "bg-amber-500", bar: "border-l-amber-500" },
  owner: { bg: "bg-rose-500/[0.18]", border: "border-rose-300", hover: "hover:bg-rose-500/25", pill: "bg-rose-600 text-white", dot: "bg-rose-500", bar: "border-l-rose-500" },
  sub: { bg: "bg-teal-500/[0.18]", border: "border-teal-300", hover: "hover:bg-teal-500/25", pill: "bg-teal-600 text-white", dot: "bg-teal-500", bar: "border-l-teal-500" },
  vendor: { bg: "bg-orange-500/[0.18]", border: "border-orange-300", hover: "hover:bg-orange-500/25", pill: "bg-orange-500 text-white", dot: "bg-orange-500", bar: "border-l-orange-500" },
  city: { bg: "bg-slate-500/[0.18]", border: "border-slate-400", hover: "hover:bg-slate-500/25", pill: "bg-slate-600 text-white", dot: "bg-slate-500", bar: "border-l-slate-500" },
  crew: { bg: "bg-lime-500/[0.18]", border: "border-lime-300", hover: "hover:bg-lime-500/25", pill: "bg-lime-600 text-white", dot: "bg-lime-500", bar: "border-l-lime-500" },
  system: { bg: "bg-slate-400/[0.18]", border: "border-slate-300 border-dashed", hover: "hover:bg-slate-400/25", pill: "bg-slate-400 text-white", dot: "bg-slate-400", bar: "border-l-slate-400" },
};
const roleTint = (r) => ROLE_TINT[r] || { bg: "bg-slate-50/70", border: "border-slate-100", hover: "hover:bg-slate-100", pill: "bg-slate-200 text-slate-600", dot: "bg-slate-300", bar: "border-l-slate-300" };

/**
 * 全链路工作台：承载阶段任务及业务流，并在标题卡上方集中展示项目级入口。
 * projectActions 接收项目页提供的分包商管理按钮；headerExtra 继续承载新建任务等工作台操作。
 * 两类入口分区后，施工计划与分包商管理使用相同的描边按钮规格，且各自保留原权限和弹窗逻辑。
 * 按需求移除八个业务流分类按钮及其筛选状态；不再读取旧的本地分类缓存，避免失去清除入口后仍隐藏业务卡或淡化任务。
 * 业务操作面板直接展示全部分类，任务关联跳转和独立的责任角色筛选继续可用。
 */
export const TraceWorkspace = ({ api, id, zh, user, onChanged, refreshTick, todaySlot, filterBarExtra, headerExtra, projectActions, sideSlot }) => {
  const [constructionPlanOpen, setConstructionPlanOpen] = useState(false);
  const nav = useNavigate();
  const [eng, setEng] = useState(null);
  const [board, setBoard] = useState(null);
  const [selPhase, setSelPhase] = useState(0);
  const [selTask, setSelTask] = useState(null);
  const [doneOpen, setDoneOpen] = useState({});
  const [roleFilter, setRoleFilter] = useState("");
  const prevSelRef = useRef(null);
  const [busy, setBusy] = useState(false);
  // 二-14：aiText / aiBusy 状态已随「AI 项目解读」功能一并移除，见按钮处注释。
  const [draftForm, setDraftForm] = useState(null);
  const [noteTask, setNoteTask] = useState(null);
  const [blkTitle, setBlkTitle] = useState(null);
  const [showBlockers, setShowBlockers] = useState(false);
  const pauseState = useProjectPause(api, id, refreshTick);
  const [assignOpen, setAssignOpen] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const [reopenReason, setReopenReason] = useState(null);
  const [previewEv, setPreviewEv] = useState(null);
  const [evDel, setEvDel] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [camOpen, setCamOpen] = useState(false);
  const [highlight, setHighlight] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [offlineCount, setOfflineCount] = useState(0);
  const [logs, setLogs] = useState([]);
  const [recState, setRecState] = useState(null);
  const [recSecs, setRecSecs] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const recSecsRef = useRef(0);
  const evInput = useRef(null);
  const camInput = useRef(null);
  const demoInput = useRef(null);
  // 【2026-10-26 权限体系改造第三批】工程面板的管理动作改为可配置动作点：
  // permOf(key) 返回管理员在「角色权限矩阵 / 账号菜单权限」里的最终判定（undefined = 尚未加载，
  // 此时回落改造前的硬编码角色名单，保证首屏/加载失败时行为与改造前一致）。
  // 为什么三个变量分开：原 MANAGE 名单实际被用于三类强度不同的动作（日常操作 / 批量催办 / 人工 Release），
  // 合成一个会让管理员无法「只给 PM 日常权限、不给 Release」，等于回到改造前的一刀切。
  const { permOf } = useApp();
  const canSchedule = permOf("/board::schedule-manage") === true;
  const canManage = permOf("/board::ops-manage") ?? MANAGE.includes(user?.role);
  const canRelease = permOf("/board::task-release") ?? RELEASE_ROLES.includes(user?.role);
  // 现场协作类（VR 打点 / 踏勘填报 / Land Survey 上传 / 811 画线）：原内联名单含 member / cdc ——
  // 这是「现场参与人」语义而非管理动作，故挂独立动作点 /board::site-collab（默认同原名单），
  // 不能并入 /board::ops-manage，否则上线当刻就会收走现场人员的能力。
  const canSiteCollab = permOf("/board::site-collab") ?? ["admin", "ceo", "eng_manager", "pm", "cdc", "member"].includes(user?.role);
  // 【2026-10-29】框架阶段两张补录任务卡的写权限，各自独立的动作点（不复用 canManage）。
  // 为什么不能复用 canManage：canManage 挂的是 /board::ops-manage（工程日常操作），
  // 而橱柜三步上传 / 衣柜尺寸录入 / 下单尺寸确认是三件不同强度的事 ——
  // 尤其 closet-confirm 默认刻意不含 pm（提交与确认分离），若复用 canManage 会把这道分离冲掉。
  // 面板内部另有服务端下发的 canWrite/canMeasure/canConfirm 做二次收口，这里只是首屏兜底。
  const canCabinetFlow = permOf("/board::cabinet-flow") ?? ["admin", "ceo", "eng_manager", "pm", "cdc"].includes(user?.role);
  const [rfq, setRfq] = useState({ items: [], map: {} });
  const loadRfq = useCallback(() => api.get(`/projects/${id}/rfq`).then(({ data }) => setRfq(data)).catch(() => {}), [api, id]);
  useEffect(() => { loadRfq(); }, [loadRfq]);
  const [tt, setTt] = useState(null);  // 任务 ↔ 工种 ↔ 分包合同（与分包卡同一数据）
  const [scopeTrade, setScopeTrade] = useState(null);
  const loadTT = useCallback(() => api.get(`/projects/${id}/trade-tasks`).then(({ data }) => setTt(data)).catch(() => {}), [api, id]);
  useEffect(() => { loadTT(); }, [loadTT]);

  const load = useCallback(async (keepTask) => {
    try {
      const [{ data: e }, { data: b }] = await Promise.all([
        api.get(`/projects/${id}/engine`), api.get(`/projects/${id}/engine-trace/board`),
      ]);
      setEng(e);
      setBoard(b);
      loadTT();
      setSelPhase((p) => (keepTask ? p : Math.min(e.phaseIdx, 7)));
      if (keepTask) {
        setSelTask((cur) => {
          if (!cur) return null;
          return (e.substages || []).flatMap((s) => s.tasks).find((x) => x.id === cur.id) || null;
        });
      }
    } catch (err) { toast.error(err?.response?.data?.detail || (zh ? "加载失败" : "Load failed")); }
  }, [api, id, zh, loadTT]);
  useEffect(() => { load(); }, [load]); // eslint-disable-line
  useEffect(() => { if (refreshTick > 0) load(true); }, [refreshTick, load]);
  const [urlParams, setUrlParams] = useSearchParams();
  // 审批通知深链和已暂停状态主动展开面板，让审批/复工入口始终可发现。
  const pauseNeedsAttention = pauseState.pause?.paused || pauseState.pause?.approvals?.some((a) => a.status === "pending");
  useEffect(() => { if (pauseNeedsAttention || urlParams.get("pause") === "1") setShowBlockers(true); }, [pauseNeedsAttention, urlParams]);
  const focusNode = urlParams.get("task");
  useEffect(() => { if (canSchedule && urlParams.get('constructionPlan') === '1') setConstructionPlanOpen(true); }, [urlParams, canSchedule]);
  useEffect(() => {  // ?task=WF-xx-xx-xx → 自动切到该阶段并展开任务（踏勘清单「返回上一步」等深链）
    if (!focusNode || !eng) return;
    const t = (eng.substages || []).flatMap((s) => s.tasks).find((x) => x.node === focusNode || x.id === focusNode);
    if (!t) return;
    setSelPhase(t.phase ?? 0);
    setSelTask(t);
    setTimeout(() => document.querySelector(`[data-testid="trace-ws-task-wrap-${t.node}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 250);
    const next = new URLSearchParams(urlParams); next.delete("task"); setUrlParams(next, { replace: true });
  }, [focusNode, eng]); // eslint-disable-line
  const loadLogs = useCallback((tid) => {
    if (!tid) { setLogs([]); return; }
    api.get(`/projects/${id}/engine/tasks/${tid}/logs`).then(({ data }) => setLogs(data)).catch(() => setLogs([]));
  }, [api, id]);
  useEffect(() => { loadLogs(selTask?.id); setReopenReason(null); setEvDel(null); }, [selTask?.id, loadLogs]);
  useEffect(() => () => { clearInterval(timerRef.current); try { mediaRef.current?.state === "recording" && mediaRef.current.stop(); } catch {} }, []);

  const act = async (fn, msg) => {
    setBusy(true);
    try {
      const { data: r } = await fn();
      if (msg) toast.success(msg);
      if (r?.advanced) toast.success(zh ? `🚀 阶段门槛全部满足，已自动推进至「${r.advanced.toName}」` : `Advanced to ${r.advanced.toName}`, { duration: 6000 });
      await load(true);
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "失败" : "Failed")); }
    setBusy(false);
  };

  const flatAll = useMemo(() => (eng ? (eng.substages || []).flatMap((s) => s.tasks) : []), [eng]);
  const payPendingByPhase = useMemo(() => {  // 阶段 → [{trade, name, count, sid, ms}]：该阶段任务关联工种中「等我处理」的付款申请
    const out = {};
    if (!tt?.subcontracts) return out;
    const nodePhase = Object.fromEntries(flatAll.map((t) => [t.node, t.phase]));
    for (const [trade, sc] of Object.entries(tt.subcontracts)) {
      if (!sc?.pendingPay) continue;
      const phases = new Set((sc.linkedNodes || []).map((n) => nodePhase[n]).filter((p) => p != null));
      if (!phases.size && eng?.phaseIdx != null) phases.add(eng.phaseIdx);
      const first = (sc.pendingPayItems || []).find((r) => r.mine);
      for (const ph of phases) (out[ph] ||= []).push({ trade, name: tt.trades?.[trade] || trade, count: sc.pendingPay, sid: sc.id, ms: first?.milestoneId });
    }
    return out;
  }, [tt, flatAll, eng]);
  const itemsByNode = useMemo(() => {
    const m = {};
    (board?.items || []).forEach((it) => { if (it.node) (m[it.node] = m[it.node] || []).push(it); });
    return m;
  }, [board]);
  const openBlockers = (eng?.blockers || []).filter((b) => b.status === "open");
  const phaseTasks = useMemo(() => (eng ? (eng.substages || []).filter((s) => s.phase === selPhase) : []), [eng, selPhase]);
  const [full, setFull] = useState(false);
  // 全屏任务链 z-[60] 会盖住分包商管理弹窗 (z-50)，且模态弹窗禁用页面点击 → 看似「死机」；深链到分包卡时先退出全屏
  const focusSubParam = urlParams.get("sub") || "";
  useEffect(() => { if (focusSubParam) setFull(false); }, [focusSubParam]);
  useEffect(() => {
    if (!full) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setFull(false); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [full]);
  // 预计应完成日（距今天数）：按阶段计划工期在任务间均分（当前工序=0），后续阶段依序累加；时间闸任务不早于解锁日
  const etaDays = useMemo(() => {
    const m = {};
    if (!eng) return m;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let base = 0;
    for (let ph = Math.max(0, Math.min(eng.phaseIdx, 7)); ph <= 7; ph++) {
      const tm = (board?.timings || [])[ph] || {};
      const planned = Number(tm.planned) || 14;
      const all = (eng.substages || []).filter((s) => s.phase === ph).flatMap((s) => s.tasks || []);
      const open = all.filter((t) => !["done", "skipped"].includes(t.state));
      const per = all.length ? planned / all.length : 0;
      open.forEach((t, i) => {
        let d = base + (i + 1) * per;
        if (t.unlock) { const u = Math.round((new Date(`${t.unlock}T00:00:00`) - today) / 86400000); if (u > d) d = u; }
        m[t.id] = t.state === "ready" ? 0 : Math.round(d);
      });
      base += open.length * per;
    }
    return m;
  }, [eng, board]);
  // 工序标题颜色：当前要做的工序 = 最红；之后按预计应完成日 红→橙→黄→绿(≈2周)→蓝(≥4周) 平滑渐变；全部完成 = 灰色
  const hueColor = (d) => {
    const hue = d <= 14 ? (d / 14) * 142 : 142 + ((Math.min(d, 28) - 14) / 14) * 79;
    return `hsl(${Math.round(hue)} 72% 28%)`;
  };
  const subEta = (s) => {
    const open = (s.tasks || []).filter((t) => !["done", "skipped"].includes(t.state));
    if (!open.length) return null;
    if (s.current || open.some((t) => t.state === "ready")) return 0;
    return Math.min(...open.map((t) => etaDays[t.id] ?? 0));
  };
  const subColor = (s) => { const d = subEta(s); return d == null ? undefined : d === 0 ? "hsl(0 90% 40%)" : hueColor(d); };
  useEffect(() => {
    // 任务刚完成（提交合格）→ 自动收起，进入本阶段下一个未完成任务
    const prev = prevSelRef.current;
    if (selTask && prev && prev.id === selTask.id && prev.state !== "done" && selTask.state === "done") {
      const ordered = phaseTasks.flatMap((s) => s.tasks || []);
      const idx = ordered.findIndex((t) => t.id === selTask.id);
      const after = ordered.slice(idx + 1).filter((t) => t.state !== "done");
      const before = ordered.slice(0, idx).filter((t) => t.state !== "done");
      const next = after.find((t) => t.state === "ready") || after[0] || before.find((t) => t.state === "ready") || before[0] || null;
      setSelTask(next);
      if (next) toast.success(zh ? `已完成 ✓ 进入下一步：${T(next.title)}` : `Next: ${next.titleEn || next.title}`);
      else toast.success(zh ? "本阶段任务已全部完成 ✓" : "Phase complete ✓");
    }
    prevSelRef.current = selTask ? { id: selTask.id, state: selTask.state } : null;
  }, [selTask, phaseTasks, zh]);
  const roleName = (r) => {
    if (!zh && eng?.rolesEn?.[r]) return eng.rolesEn[r];
    const v = eng?.roles?.[r];
    return T(typeof v === "string" ? v : (v?.zh || v?.name || (r || "").toUpperCase()));
  };

  const flushOffline = useCallback(async () => {
    if (!navigator.onLine) return;
    let pend = [];
    try { pend = (await listEvidence()).filter((x) => x.projectId === id); } catch { return; }
    if (!pend.length) { setOfflineCount(0); return; }
    let ok = 0;
    for (const it of pend) {
      try {
        const f = new File([it.blob], it.name, { type: it.type });
        const up = await chunkUpload(api, f);
        await api.post(`/projects/${id}/engine/tasks/${it.taskId}/evidence`, up);
        await removeEvidence(it.id);
        ok += 1;
      } catch { break; }
    }
    setOfflineCount(pend.length - ok);
    if (ok) {
      toast.success(zh ? `✅ 已自动上传 ${ok} 张离线缓存的证据照片` : `Uploaded ${ok} cached photos`);
      load(true);
      onChanged && onChanged();
    }
  }, [api, id, zh, load, onChanged]);
  useEffect(() => {
    listEvidence().then((l) => setOfflineCount(l.filter((x) => x.projectId === id).length)).catch(() => {});
    flushOffline();
    window.addEventListener("online", flushOffline);
    return () => window.removeEventListener("online", flushOffline);
  }, [flushOffline, id]);

  const shareOwner = async () => {
    try {
      const { data } = await api.post(`/projects/${id}/engine-trace/share`);
      const full = `${api.defaults.baseURL}/public/trace-report/${data.token}`;
      try {
        await navigator.clipboard.writeText(full);
        toast.success(zh ? "业主报告链接已复制，发给业主即可免登录查看进度报告" : "Owner report link copied");
      } catch { window.prompt(zh ? "复制业主报告链接：" : "Copy link:", full); }
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "生成分享链接失败" : "Failed")); }
  };

  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      // 分类筛选已移除，导出采用接口默认的全部业务流口径，与页面保持一致。
      const { data } = await api.get(`/projects/${id}/engine-trace/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = T("全链路报告_{0}.pdf", [id]);
      a.click();
      URL.revokeObjectURL(url);
      toast.success(zh ? "PDF 报告已下载" : "PDF downloaded");
    } catch { toast.error(zh ? "PDF 生成失败" : "PDF failed"); }
    setPdfBusy(false);
  };

  const startRec = async () => {
    if (!navigator.mediaDevices?.getUserMedia) { toast.error(zh ? "当前浏览器不支持录音" : "Not supported"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const opts = window.MediaRecorder?.isTypeSupported?.("audio/webm") ? { mimeType: "audio/webm" } : undefined;
      const mr = new MediaRecorder(stream, opts);
      chunksRef.current = [];
      const taskId = selTask?.id;
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 800) { setRecState(null); toast.error(zh ? "录音太短" : "Too short"); return; }
        setRecState("processing");
        try {
          const file = new File([blob], `voice_${Date.now()}.webm`, { type: blob.type || "audio/webm" });
          const up = await chunkUpload(api, file);
          const { data: log } = await api.post(`/projects/${id}/engine/tasks/${taskId}/voice-log`, { ...up, durationSec: recSecsRef.current });
          setLogs((l) => [log, ...l]);
          toast.success(zh ? "语音已自动转写为进度日志 ✓" : "Transcribed ✓");
        } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "转写失败" : "Failed")); }
        setRecState(null);
      };
      mr.start(250);
      mediaRef.current = mr;
      recSecsRef.current = 0;
      setRecSecs(0);
      setRecState("rec");
      timerRef.current = setInterval(() => { recSecsRef.current += 1; setRecSecs(recSecsRef.current); }, 1000);
    } catch { toast.error(zh ? "无法访问麦克风，请授权后重试" : "Mic access denied"); }
  };
  const stopRec = () => { clearInterval(timerRef.current); try { mediaRef.current?.stop(); } catch {} };

  // 二-14：原 runAi（调用 /engine-trace/ai-interpret 生成 AI 项目解读）已移除，
  // 与页面顶部 AICard「AI 项目诊断 · 主动预警」功能重复，见按钮处注释。

  // 业务卡已直接展示；关联项只负责定位或高亮，不再暗中改变分类可见范围。
  const jumpToItem = (it) => {
    if (it.cat === "comm") {
      setHighlight({ id: it.id, rule: it.rule });
      setTimeout(() => document.getElementById("ops-comm-card")?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
      return;
    }
    if (it.node) {
      const t = flatAll.find((x) => x.node === it.node);
      if (t) { setSelPhase(t.phase); setSelTask(t); return; }
    }
    if (it.phase != null) setSelPhase(it.phase);
    setTimeout(() => document.getElementById("trace-ops-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  };

  // 二-13：原 printReport（打开新窗口写 HTML 并调用 window.print）已删除——
  // 与「导出PDF」功能重叠，统一保留 exportPdf（下载 PDF 文件，便于分享/存档）。
  // 如需打印，用户可在 PDF 阅读器中自行打印。

  // ---------- 任务动作（原引擎面板全量） ----------
  const doComplete = (t) => setNoteTask(t);
  const doSkip = (t) => {
    const reason = window.prompt(zh ? "跳过原因（本项目不适用）：" : "Skip reason:", T("本项目不适用"));
    if (reason === null) return;
    act(() => api.post(`/projects/${id}/engine/tasks/${t.id}/skip`, { reason }), zh ? "已跳过" : "Skipped");
  };
  const doWaiting = (t) => {
    const note = window.prompt(zh ? "等待外部说明（如：等待市府回复）" : "Waiting note:", T("等待外部回复"));
    if (note === null) return;
    act(() => api.post(`/projects/${id}/engine/tasks/${t.id}/waiting`, { note }), zh ? "已标记等待外部" : "Marked waiting");
  };
  const doResume = (t) => act(() => api.post(`/projects/${id}/engine/tasks/${t.id}/waiting`, { clear: true }), zh ? "已恢复待办" : "Cleared");
  const doReopen = (t) => {
    if (!canManage) { toast.error(zh ? "仅管理层（admin / CEO / 工程经理 / PM）可重开任务" : "Managers only"); return; }
    if (!["done", "skipped", "waiting_external"].includes(t.status)) { toast.info(zh ? "该任务尚未完成，当前已是待办状态，无需重开" : "Task is already open"); return; }
    setReopenReason((v) => (v === null ? "" : null));
  };
  const submitReopen = async () => {
    const reason = (reopenReason || "").trim();
    if (!reason) { toast.error(zh ? "请填写重开原因" : "Reason required"); return; }
    await act(() => api.post(`/projects/${id}/engine/tasks/${selTask.id}/reopen`, { reason }), zh ? "已重开并记入任务日志，已通知原完成人/责任人" : "Reopened");
    setReopenReason(null);
    loadLogs(selTask.id);
  };
  const submitEvDel = async () => {
    if (!evDel?.ev) return;
    const tid = selTask.id;
    setBusy(true);
    try {
      const { data: r } = await api.delete(`/projects/${id}/engine/tasks/${tid}/evidence/${evDel.ev.fileId}`, { params: { reason: (evDel.reason || "").trim() } });
      toast.success(zh ? `证据已删除并记入日志（剩余 ${r.evidenceLeft} 份）` : `Removed (${r.evidenceLeft} left)`);
      if (r.needsEvidence) toast.warning(zh ? "该任务要求证据且已无证据，请补传或重开任务" : "No evidence left on a completed task", { duration: 6000 });
      setEvDel(null);
      await load(true);
      onChanged && onChanged();
      loadLogs(tid);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "删除失败" : "Failed")); }
    setBusy(false);
  };
  const doNudge = (t) => act(() => api.post(`/projects/${id}/engine/tasks/${t.id}/nudge`), zh ? "已催办" : "Nudged");
  const doRelease = (t) => act(() => api.post(`/projects/${id}/engine/release`, { code: t.node, note: T("人工 Release") }), zh ? "已人工 Release，时间闸放行" : "Released");
  const doAssign = (t, username) => { setAssignOpen(false); act(() => api.post(`/projects/${id}/engine/tasks/${t.id}/assign`, { username }), username ? (zh ? "已指派并通知本人" : "Assigned") : (zh ? "已取消指派" : "Unassigned")); };
  const setGate = (k, v) => act(() => api.post(`/projects/${id}/engine/gates`, { [k]: v }));
  const demoYes = () => act(() => api.post(`/projects/${id}/engine/demo-decision`, { needed: true }), zh ? "已确认：需要拆除" : "Demo needed");
  const demoNoFile = async (file) => {
    try {
      const up = await chunkUpload(api, file);
      await act(() => api.post(`/projects/${id}/engine/demo-decision`, { needed: false, waiverFileId: up.fileId }), zh ? "已确认无需拆除，豁免文件已留档" : "No demo, waiver filed");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "上传失败" : "Upload failed")); }
  };
  const doEvidence = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length || !selTask) return;
    const tid = selTask.id;
    const rows = list.map((f, i) => ({ key: `${Date.now()}_${i}`, name: f.name, size: f.size, pct: 0, status: "queued", taskId: tid, file: f }));
    setUploads((u) => [...u.filter((r) => r.status === "uploading" || r.status === "queued"), ...rows]);
    const patch = (key, p) => setUploads((u) => u.map((r) => (r.key === key ? { ...r, ...p } : r)));
    setBusy(true);
    let ok = 0, cached = 0, failed = 0;
    for (let i = 0; i < list.length; i++) {
      const file = list[i], key = rows[i].key;
      patch(key, { status: "uploading" });
      try {
        if (!navigator.onLine) { const e = new Error("offline"); e.offline = true; throw e; }
        const up = await chunkUpload(api, file, (pct) => patch(key, { pct }));
        await api.post(`/projects/${id}/engine/tasks/${tid}/evidence`, up);
        patch(key, { status: "done", pct: 100 });
        ok += 1;
      } catch (e) {
        if (e.offline || !navigator.onLine || e.code === "ERR_NETWORK" || !e.response) {
          try {
            await queueEvidence({ id: `OF${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, projectId: id,
              taskId: tid, name: file.name, type: file.type || "image/jpeg", blob: file, at: Date.now() });
            setOfflineCount((n) => n + 1);
            patch(key, { status: "offline" });
            cached += 1;
          } catch { patch(key, { status: "error", error: zh ? "离线缓存失败" : "Cache failed" }); failed += 1; }
        } else {
          patch(key, { status: "error", error: errTextOf(e?.response?.data?.detail) || (zh ? "上传失败" : "Upload failed") });
          failed += 1;
        }
      }
    }
    if (ok) toast.success(zh ? (list.length > 1 ? T("已上传 {0}/{1} 份证据 ✓", [ok, list.length]) : T("证据已上传 ✓")) : `Uploaded ${ok}/${list.length}`);
    if (cached) toast.info(zh ? `📴 无网络，${cached} 份已离线缓存，恢复网络后自动上传` : `${cached} cached offline`);
    if (failed) toast.error(zh ? `${failed} 份上传失败，可点「重试」` : `${failed} failed`);
    if (ok) { await load(true); onChanged && onChanged(); }
    setBusy(false);
    if (evInput.current) evInput.current.value = "";
    if (camInput.current) camInput.current.value = "";
    if (!failed && !cached) setTimeout(() => setUploads((u) => u.filter((r) => !rows.some((x) => x.key === r.key))), 4000);
  };
  const retryUpload = (row) => {
    setUploads((u) => u.filter((r) => r.key !== row.key));
    if (row.file) doEvidence([row.file]);
  };
  const openDraft = () => setDraftForm({ open: true });
  const commPayload = (p) => ({ channel: p.channel, body: p.body, toRole: "custom", to: p.to || "", toName: p.toName || "",
    subject: p.channel === "email" ? (p.subject || `${id} · ${selTask?.title?.slice(0, 30)}`) : "" });
  const doDraft = (p) => act(async () => {
    const res = await api.post(`/projects/${id}/engine/comms`, commPayload(p));
    setDraftForm(null);
    return res;
  }, zh ? "联动消息草稿已创建，请到通信中心确认发送" : "Draft created");
  const doDraftSend = (p) => act(async () => {
    const { data: c } = await api.post(`/projects/${id}/engine/comms`, commPayload(p));
    const res = await api.post(`/projects/${id}/engine/comms/${c.id}/send`);
    setDraftForm(null);
    toast.info(res.data?.status === "logged" ? (zh ? "测试模式：已留痕未真实外发" : "Test mode: logged") : (zh ? `已发送 → ${res.data?.sentTo || ""}` : "Sent"));
    return res;
  }, zh ? "审核通过，消息已提交发送" : "Sent");
  const closeBlocker = (b) => {
    const resolution = window.prompt(zh ? "解决说明：" : "Resolution:", T("已解决"));
    if (resolution === null) return;
    act(() => api.post(`/projects/${id}/engine/blockers/${b.id}/close`, { resolution }), zh ? "阻塞项已关闭" : "Closed");
  };

  if (!eng || !board) return <div className="bg-white rounded-xl border border-slate-200 p-8 flex justify-center" data-testid="trace-ws-loading"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>;


  const detailBody = selTask && (() => {
    const nodeItems = itemsByNode[selTask.node] || [];
    const canActPrimary = ["ready", "pending"].includes(selTask.state);
    return (
      <div className="space-y-3">
        {/* 节点进度入口与全流程计划使用同一施工日程权限，避免授权后仍看不到调整入口。 */}
        {canSchedule && <PlanNodeProgress api={api} projectId={id} node={selTask.node} onOpen={() => setConstructionPlanOpen(true)} />}
        {selTask.assignee && <div className="flex flex-wrap gap-1"><span className="text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700" data-testid="trace-ws-detail-assignee">{zh ? "已指派" : "Assigned"} · {selTask.assignee}</span></div>}
        {nodeItems.length > 0 && (
          <div className="rounded-lg bg-slate-50 border border-slate-100 p-2.5" data-testid="trace-ws-node-links">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">{zh ? "本节点关联业务流" : "Linked records"}</p>
            {nodeItems.map((it) => (
              <button key={it.id} onClick={() => jumpToItem(it)} className="w-full text-left flex items-center gap-1.5 text-[11px] text-slate-600 py-0.5 hover:text-indigo-600">
                <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${CAT_DOT[it.cat]}`} />
                <span className="truncate flex-1">{T(it.title)}</span>
                <span className="text-[9px] text-slate-400 shrink-0">{zh ? it.statusZh : (it.status || T(it.statusZh))}</span>
              </button>
            ))}
          </div>
        )}
        <div data-testid="trace-ws-detail-actions">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{zh ? "允许的下一动作" : "Allowed actions"}</p>
            <button data-testid="trace-ws-steps-toggle" onClick={() => setShowSteps((v) => !v)} className="text-[10px] text-indigo-500 hover:underline">{showSteps ? (zh ? "收起操作步骤" : "Hide steps") : (zh ? "操作步骤" : "How to")}</button>
          </div>
          {showSteps && (
            <div className="mb-2 rounded-lg bg-indigo-50/60 border border-indigo-100 p-2.5 space-y-1" data-testid="trace-ws-steps-guide">
              {ACTION_STEPS.map(([k, label, steps]) => (
                <div key={k} className="text-[10px] leading-relaxed" data-testid={`trace-ws-steps-${k}`}>
                  <span className="font-semibold text-indigo-700">{zh ? label[0] : label[1]}</span>
                  <span className="text-slate-500">：{(zh ? steps[0] : steps[1]).map((s, i) => `${i + 1}. ${s}`).join(" → ")}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-1.5" data-testid="trace-ws-standard-actions">
            <button data-testid="trace-ws-act-evidence" disabled={busy} onClick={() => evInput.current?.click()}
              className="text-[11px] px-3 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-700 inline-flex items-center gap-1"><Upload className="h-3 w-3" />{zh ? "上传证据（可多选）" : "Evidence (multi)"}</button>
            <button data-testid="trace-ws-act-camera" disabled={busy} onClick={() => setCamOpen(true)}
              className="text-[11px] px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 inline-flex items-center gap-1"><Camera className="h-3 w-3" />{zh ? "拍照上传（连拍）" : "Camera (burst)"}</button>
            <button data-testid="trace-ws-act-reopen" disabled={busy} onClick={() => doReopen(selTask)}
              title={!canManage ? (zh ? "仅管理层可重开" : "Managers only") : (["done", "skipped", "waiting_external"].includes(selTask.status) ? "" : (zh ? "任务尚未完成，当前已是待办" : "Already open"))}
              className={`text-[11px] px-3 py-2 rounded-lg inline-flex items-center gap-1 ${["done", "skipped", "waiting_external"].includes(selTask.status) && canManage ? "bg-slate-700 text-white hover:bg-slate-600" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}><RotateCcw className="h-3 w-3" />{zh ? "重开" : "Reopen"}</button>
            <button data-testid="trace-ws-act-draft" disabled={busy} onClick={openDraft}
              className="text-[11px] px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"><MessageSquare className="h-3 w-3" />{zh ? "起草联动消息" : "Draft comm"}</button>
            <button data-testid="trace-ws-act-blocker" disabled={busy || !pauseState.pause?.canRequest || !!pauseState.error} onClick={() => { setFull(false); setShowBlockers(true); setBlkTitle(selTask.title?.slice(0, 1000) || ""); }}
              className="text-[11px] px-3 py-2 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"><AlertTriangle className="h-3 w-3" aria-hidden="true" />{zh ? "暂停项目" : "Pause project"}</button>
          </div>
          {uploads.some((r) => r.taskId === selTask.id) && (
            <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50/70 p-2 space-y-1.5" data-testid="trace-ws-upload-panel">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-semibold text-slate-600">{zh ? `批量上传进度 · ${uploads.filter((r) => r.taskId === selTask.id && r.status === "done").length}/${uploads.filter((r) => r.taskId === selTask.id).length} 完成` : `Uploads · ${uploads.filter((r) => r.taskId === selTask.id && r.status === "done").length}/${uploads.filter((r) => r.taskId === selTask.id).length}`}</p>
                {!busy && <button data-testid="trace-ws-upload-clear" onClick={() => setUploads((u) => u.filter((r) => r.taskId !== selTask.id))} className="text-[10px] text-slate-400 hover:underline">{zh ? "清除" : "Clear"}</button>}
              </div>
              {uploads.filter((r) => r.taskId === selTask.id).map((r, i) => (
                <div key={r.key} className="text-[10px]" data-testid={`trace-ws-upload-row-${i}`}>
                  <div className="flex items-center gap-1.5">
                    {r.status === "uploading" && <Loader2 className="h-3 w-3 animate-spin text-indigo-500 shrink-0" />}
                    {r.status === "done" && <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />}
                    {r.status === "error" && <XCircle className="h-3 w-3 text-red-500 shrink-0" />}
                    {r.status === "offline" && <WifiOff className="h-3 w-3 text-amber-500 shrink-0" />}
                    {r.status === "queued" && <span className="h-3 w-3 rounded-full border border-slate-300 shrink-0" />}
                    <span className="truncate flex-1 text-slate-700" title={r.name}>{T(r.name)}</span>
                    <span className="text-slate-400 shrink-0">{r.size > 1048576 ? `${(r.size / 1048576).toFixed(1)}MB` : `${Math.max(1, Math.round(r.size / 1024))}KB`}</span>
                    <span data-testid={`trace-ws-upload-pct-${i}`} className={`w-9 text-right shrink-0 font-mono ${r.status === "error" ? "text-red-600" : r.status === "done" ? "text-emerald-600" : "text-slate-600"}`}>
                      {r.status === "error" ? (zh ? "失败" : "ERR") : r.status === "offline" ? (zh ? "离线" : "OFF") : r.status === "queued" ? (zh ? "排队" : "…") : `${r.pct}%`}</span>
                    {r.status === "error" && <button data-testid={`trace-ws-upload-retry-${i}`} onClick={() => retryUpload(r)} className="text-indigo-600 hover:underline shrink-0">{zh ? "重试" : "Retry"}</button>}
                  </div>
                  <div className="h-1 rounded-full bg-slate-200 mt-0.5 overflow-hidden">
                    <div className={`h-full transition-[width] duration-200 ${r.status === "error" ? "bg-red-400" : r.status === "done" ? "bg-emerald-500" : r.status === "offline" ? "bg-amber-400" : "bg-indigo-500"}`} style={{ width: `${r.status === "done" ? 100 : r.status === "error" || r.status === "offline" ? 100 : r.pct}%` }} />
                  </div>
                  {r.error && <p className="text-[9px] text-red-500 mt-0.5">{errTextOf(r.error)}</p>}
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-1.5 mt-1.5 empty:hidden" data-testid="trace-ws-state-actions">
            {canActPrimary && (!selTask.requiresEvidence || (selTask.evidence || []).length > 0) && (
              <button data-testid="trace-ws-act-complete" disabled={busy} onClick={() => doComplete(selTask)}
                className="text-[11px] px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />{zh ? "完成打卡" : "Complete"}</button>
            )}
            {canActPrimary && (
              <button data-testid="trace-ws-act-waiting" disabled={busy} onClick={() => doWaiting(selTask)}
                className="text-[11px] px-3 py-2 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200">{zh ? "等外部" : "Wait ext."}</button>
            )}
            {canActPrimary && selTask.optional && (
              <button data-testid="trace-ws-act-skip" disabled={busy} onClick={() => doSkip(selTask)}
                className="text-[11px] px-3 py-2 rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200">{zh ? "跳过" : "Skip"}</button>
            )}
            {selTask.state === "waiting_external" && (
              <>
                <button data-testid="trace-ws-act-resume" disabled={busy} onClick={() => doResume(selTask)} className="text-[11px] px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">{zh ? "已回复·恢复" : "Resume"}</button>
                <button data-testid="trace-ws-act-complete2" disabled={busy} onClick={() => doComplete(selTask)} className="text-[11px] px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700">{zh ? "完成" : "Done"}</button>
              </>
            )}
            {selTask.state === "waiting_time" && canRelease && (
              <button data-testid="trace-ws-act-release" disabled={busy} onClick={() => doRelease(selTask)} className="text-[11px] px-3 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 inline-flex items-center gap-1"><Unlock className="h-3 w-3" />Release</button>
            )}
            {["blocked", "waiting_external"].includes(selTask.state) && (
              <button data-testid="trace-ws-act-nudge" disabled={busy} onClick={() => doNudge(selTask)} className="text-[11px] px-3 py-2 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200">{zh ? "催办" : "Nudge"}</button>
            )}
            {canManage && !["done", "skipped"].includes(selTask.status) && (
              <button data-testid="trace-ws-act-assign" disabled={busy} onClick={() => setAssignOpen(!assignOpen)} className="text-[11px] px-3 py-2 rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50">{selTask.assignee ? `👤 ${selTask.assignee}` : (zh ? "指派" : "Assign")} <CoTag kind={selTask.assigneeKind} zh={zh} /></button>
            )}
          </div>
          {assignOpen && (
            <select autoFocus data-testid="trace-ws-assign-select" value={selTask.assigneeUsername || ""} onChange={(e) => doAssign(selTask, e.target.value)}
              className="mt-2 w-full text-xs rounded-lg border border-indigo-200 px-2 py-1.5 bg-white">
              <option value="">{zh ? "— 取消指派 —" : "Unassigned"}</option>
              <AssigneeOptions projectStaff={eng.projectStaff} staff={eng.staff} zh={zh} T={T} />
            </select>
          )}
          {draftForm && selTask && (
            <DraftCommPanel api={api} pid={id} task={selTask} zh={zh} canManage={canManage} busy={busy} onClose={() => setDraftForm(null)} onDraft={doDraft} onSend={doDraftSend} />
          )}
          {reopenReason !== null && (
            <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50/50 p-2.5 space-y-1.5" data-testid="trace-ws-reopen-form">
              <p className="text-[10px] font-semibold text-amber-800 flex items-center gap-1"><RotateCcw className="h-3 w-3" />{zh ? "重开原因（必填，将记入任务日志便于追溯）" : "Reopen reason (required, logged)"}</p>
              <textarea autoFocus data-testid="trace-ws-reopen-reason" value={reopenReason} onChange={(e) => setReopenReason(e.target.value.slice(0, 200))} rows={2}
                className="w-full text-[12px] border border-amber-200 rounded-md px-2 py-1.5 bg-white" placeholder={zh ? "例：市检未通过需整改后重新拍照 / 上传的证据照片不清晰" : "e.g. inspection failed, redo"} />
              <div className="flex gap-1.5 justify-end items-center">
                <span className="text-[9px] text-slate-400 mr-auto">{(reopenReason || "").length}/200</span>
                <button onClick={() => setReopenReason(null)} className="text-[11px] text-slate-400 hover:underline">{zh ? "取消" : "Cancel"}</button>
                <button data-testid="trace-ws-reopen-confirm" disabled={busy || !reopenReason.trim()} onClick={submitReopen} className="text-[11px] px-2.5 py-1 rounded-md bg-amber-600 text-white disabled:opacity-50">{zh ? "确认重开" : "Reopen"}</button>
              </div>
            </div>
          )}

        </div>
        {String(selTask.node || "").startsWith("WF-01-02") && (
          <>
            <VrPanel api={api} pid={id} task={selTask} zh={zh} canManage={canManage} canMark={canSiteCollab} />
            <SurveyPanel api={api} pid={id} zh={zh} canFill={canSiteCollab} />
          </>
        )}
        {selTask.node === "WF-02-03-03" && (
          <LandSurveyPanel api={api} pid={id} zh={zh} canUpload={canSiteCollab} canDelete={canManage} onChanged={async () => { await load(true); onChanged && onChanged(); }} />
        )}
        {(selTask.node === "WF-03-01-01" || selTask.node === "WF-03-01-02") && (
          <SiteOrderPanel api={api} pid={id} kind={selTask.node === "WF-03-01-01" ? "fence" : "toilet"} zh={zh} onChanged={async () => { await load(true); onChanged && onChanged(); }} />
        )}
        {selTask.node === "WF-07-02-06" && (
          <SiteRemovalPanel api={api} pid={id} zh={zh} onChanged={async () => { await load(true); onChanged && onChanged(); }} />
        )}
        {/* 【2026-10-29】框架阶段两张补录任务卡：面板本身按服务端下发的 canWrite/canMeasure/canConfirm
            控制可写性，前端不按角色硬编码（权限走 /board::cabinet-flow、/board::closet-measure、/board::closet-confirm）。 */}
        {selTask.node === "WF-04-01-07" && (
          <CabinetFlowPanel api={api} pid={id} zh={zh} canWrite={canCabinetFlow} onChanged={async () => { await load(true); onChanged && onChanged(); }} />
        )}
        {selTask.node === "WF-04-01-08" && (
          <ClosetOpeningsPanel api={api} pid={id} zh={zh} onChanged={async () => { await load(true); onChanged && onChanged(); }} />
        )}
        {(selTask.evidence || []).length > 0 && (
          <div data-testid="trace-ws-evidence-section">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5" data-testid="trace-ws-evidence-count">📎 {zh ? `已上传证据 ${(selTask.evidence || []).length} 份 · 点击预览大图` : `${(selTask.evidence || []).length} files · click to preview`}</p>
            <div className="flex flex-wrap gap-1.5" data-testid="trace-ws-evidence-grid">
              {(selTask.evidence || []).map((e, x) => {
                const isImg = (e.contentType || "").startsWith("image");
                const marked = evDel?.ev?.fileId === e.fileId;
                // 需求（走查清单 一-3，视频 47:39–47:53）：上传材料可能丢包，必须显示序号便于核对是否丢失。
                // 优先用后端分配的稳定 seq；老数据没有 seq 时回退用当前数组位置 x+1 兜底。
                const seqNo = e.seq || (x + 1);
                return (
                  <div key={`${e.fileId}-${x}`} className="relative">
                    <span data-testid={`trace-ws-evidence-seq-${x}`} title={zh ? `序号 ${seqNo}（用于核对是否丢包）` : `No. ${seqNo}`}
                      className="absolute top-0 left-0 z-10 min-w-[16px] h-[16px] px-1 rounded-br-md rounded-tl-lg bg-indigo-600 text-white text-[9px] font-bold flex items-center justify-center tabular-nums">{seqNo}</span>
                    <button data-testid={`trace-ws-evidence-thumb-${x}`} onClick={() => setPreviewEv(e)}
                      title={`${e.filename || ""}${e.by ? ` · ${e.by}` : ""}${e.at ? ` · ${fmtDT(e.at)}` : ""}`}
                      className={`group relative h-20 w-20 rounded-lg overflow-hidden border bg-slate-50 hover:shadow-md transition-[border-color,box-shadow] ${marked ? "border-red-500 ring-2 ring-red-200" : "border-slate-200 hover:border-indigo-400"}`}>
                      {isImg ? (
                        <img src={evidenceUrl(e.fileId)} alt={e.filename || "evidence"} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform" />
                      ) : (
                        <div className="h-full w-full flex flex-col items-center justify-center gap-1 p-1 text-slate-500">
                          <FileText className="h-5 w-5" /><span className="text-[8px] leading-tight truncate w-full text-center">{(e.filename || "file").split(".").pop().toUpperCase()}</span>
                        </div>
                      )}
                      <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[8px] px-1 py-0.5 truncate text-left">{e.by || ""}{e.at ? ` ${String(e.at).slice(5, 10)}` : ""}</span>
                    </button>
                    {canManage && (
                      <button data-testid={`trace-ws-evidence-del-${x}`} disabled={busy} title={zh ? "删除误传证据（管理层）" : "Remove evidence"}
                        onClick={(ev) => { ev.stopPropagation(); setEvDel(marked ? null : { ev: e, reason: "" }); }}
                        className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-white border border-red-300 text-red-600 shadow flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors"><X className="h-3 w-3" /></button>
                    )}
                  </div>
                );
              })}
            </div>
            {evDel && (
              <div className="mt-2 rounded-lg border border-red-300 bg-red-50/60 p-2.5 space-y-1.5" data-testid="trace-ws-evidence-del-form">
                <p className="text-[10px] font-semibold text-red-700 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{zh ? `删除证据「${evDel.ev.filename || evDel.ev.fileId}」？删除后不再进入业主报告与项目文件区，操作将记入任务日志。` : `Remove "${evDel.ev.filename || ""}"? Logged; removed from owner report.`}</p>
                <input data-testid="trace-ws-evidence-del-reason" value={evDel.reason} onChange={(e) => setEvDel((d) => ({ ...d, reason: e.target.value.slice(0, 200) }))}
                  className="w-full text-[12px] border border-red-200 rounded-md px-2 py-1.5 bg-white" placeholder={zh ? "删除原因（选填，默认「误传」）例：拍错项目 / 照片模糊" : "Reason (optional)"} />
                <div className="flex gap-1.5 justify-end">
                  <button onClick={() => setEvDel(null)} className="text-[11px] text-slate-400 hover:underline">{zh ? "取消" : "Cancel"}</button>
                  <button data-testid="trace-ws-evidence-del-confirm" disabled={busy} onClick={submitEvDel} className="text-[11px] px-2.5 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">{zh ? "确认删除" : "Remove"}</button>
                </div>
              </div>
            )}
          </div>
        )}
        <div data-testid="trace-ws-voice-section">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">{zh ? "任务日志 · 现场语音记录（自动转写）/ 完成留言 / 重开原因 / 证据删除" : "Task log · voice / notes / reopen / evidence"}</p>
          <div className="flex items-center gap-2 flex-wrap">
            {recState === "rec" ? (
              <button data-testid="trace-ws-voice-stop" onClick={stopRec}
                className="text-[11px] px-3 py-2 rounded-lg bg-brand-500 text-white inline-flex items-center gap-1.5 animate-pulse">
                <Square className="h-3 w-3 fill-current" />{zh ? `停止录音 ${recSecs}s` : `Stop ${recSecs}s`}</button>
            ) : recState === "processing" ? (
              <span data-testid="trace-ws-voice-processing" className="text-[11px] px-3 py-2 rounded-lg bg-slate-100 text-slate-500 inline-flex items-center gap-1.5">
                <Loader2 className="h-3 w-3 animate-spin" />{zh ? "转写中…" : "Transcribing…"}</span>
            ) : (
              <button data-testid="trace-ws-voice-rec" disabled={busy} onClick={startRec}
                className="text-[11px] px-3 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 inline-flex items-center gap-1.5">
                <Mic className="h-3 w-3" />{zh ? "开始语音记录" : "Record"}</button>
            )}
            {logs.length > 0 && <span className="text-[10px] text-slate-400">{zh ? `${logs.length} 条日志` : `${logs.length} logs`}</span>}
          </div>
          {logs.length > 0 && (
            <div className="mt-2 space-y-1.5 max-h-44 overflow-y-auto pr-1" data-testid="trace-ws-voice-logs">
              {logs.map((l) => (
                <div key={l.id} className={`rounded-lg border p-2 ${l.kind === "reopen" ? "bg-amber-50 border-amber-200" : l.kind === "evidence_removed" ? "bg-red-50 border-red-200" : "bg-rose-50/60 border-rose-100"}`} data-testid={`trace-ws-voice-log-${l.id}`}>
                  <p className="text-[11px] text-slate-700 leading-relaxed flex items-start gap-1">
                    {l.kind === "reopen" && <RotateCcw className="h-3 w-3 text-amber-600 shrink-0 mt-0.5" />}
                    {l.kind === "evidence_removed" && <AlertTriangle className="h-3 w-3 text-red-600 shrink-0 mt-0.5" />}<span>{T(l.text)}</span></p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[9px] text-slate-400">{l.by} · {fmtDT(l.at)}{l.durationSec ? ` · ${l.durationSec}s` : ""}</span>
                    {l.audioFileId && (
                      <audio controls preload="none" className="h-6 max-w-[150px]"
                        src={`${api.defaults.baseURL}/files/${l.audioFileId}/download?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`} />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {selTask.duty && (
          <div className="rounded-lg bg-slate-50 border border-slate-100 p-2.5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">{zh ? "职责说明（知识库）" : "Duty"}</p>
            <p className="text-[11px] text-slate-500 leading-relaxed">{T(selTask.duty).slice(0, 180)}{T(selTask.duty).length > 180 ? "…" : ""}</p>
          </div>
        )}
      </div>
    );
  })();

  return (
    <div className="space-y-4" data-testid="trace-ws-page">
      {canSchedule && constructionPlanOpen && <ConstructionPlan projectId={id} onClose={() => setConstructionPlanOpen(false)} onChanged={() => load(true)} />}
      {/* 项目级入口独立成组，窄屏允许换行；分包商入口继续由各自权限控制。 */}
      {(canSchedule || projectActions) && (
        <div role="group" aria-label={zh ? "项目管理入口" : "Project management"} className="flex flex-wrap items-center gap-2" data-testid="trace-ws-project-actions">
          {canSchedule && (
            <Button type="button" variant="outline" data-testid="trace-ws-construction-plan-btn" className="min-h-11 h-auto whitespace-normal text-left" aria-haspopup="dialog" onClick={() => setConstructionPlanOpen(true)}>
              <CalendarRange aria-hidden="true" />{zh ? "全流程施工计划" : "Full Construction Plan"}
            </Button>
          )}
          {projectActions}
        </div>
      )}
      {/* 顶部标题与动作 */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5"><GitBranch className="h-4 w-4 text-red-600" />{zh ? "全链路追踪工作台" : "Chain Trace Workspace"}
              {/* 【2026-10-29】任务数改为按服务端数据实时统计，不再写死 81（实际数早已漂移）。 */}
              <span className="text-[9px] font-normal text-slate-300">{eng.version} · {zh
                ? `${(eng.phases || []).length}阶段/${(eng.substages || []).reduce((n, s) => n + (s.tasks || []).length, 0)}任务卡/8类业务流`
                : `${(eng.phases || []).length} phases / ${(eng.substages || []).reduce((n, s) => n + (s.tasks || []).length, 0)} cards`}</span>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {offlineCount > 0 && (
              <button data-testid="trace-ws-offline-badge" onClick={flushOffline}
                className="text-[11px] font-semibold rounded-full px-2.5 py-1.5 bg-amber-100 text-amber-700 inline-flex items-center gap-1">
                <WifiOff className="h-3 w-3" />{zh ? `离线待传 ${offlineCount}` : `Offline ${offlineCount}`}
              </button>
            )}
            <ProjectPauseButton pause={pauseState.pause} open={showBlockers} onClick={() => setShowBlockers(!showBlockers)} zh={zh} />
            {headerExtra}
            {/* 二-14：「AI 项目解读」已移除——与页面顶部 AICard「AI 项目诊断 · 主动预警」功能重复。
                老板原话（39:22-40:22）：这个 AI 项目解读跟前面那个 AI 项目诊断的地方差不多，
                背后逻辑可能不一样，但用户看来都是 AI 分析项目，拿掉一个即可；直接把这个删了就好。
                保留顶部 AICard（结构化诊断：延误天数/原因/置信度/赶工计划，功能更完整）。 */}
            {/* 二-13：「打印报告」与「导出PDF」功能重叠（老板原话：打印报告本质就是调打印窗口，导出PDF同样能出报告，二者留一个即可）。
                保留「导出PDF」（可下载文件，便于分享/存档），删除「打印报告」按钮及 printReport 函数。 */}
            <Button data-testid="trace-ws-pdf-btn" size="sm" variant="outline" onClick={exportPdf} disabled={pdfBusy} className="h-8 text-xs">
              {pdfBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileDown className="h-3.5 w-3.5 mr-1" />}{zh ? "导出PDF" : "PDF"}</Button>
            {canManage && (
              <Button data-testid="trace-ws-share-btn" size="sm" variant="outline" onClick={shareOwner} className="h-8 text-xs">
                <Share2 className="h-3.5 w-3.5 mr-1" />{zh ? "分享业主" : "Share"}</Button>
            )}
            <button data-testid="trace-ws-refresh" onClick={() => load(true)} className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"><RefreshCw className="h-4 w-4" /></button>
          </div>
        </div>
        {/* 二-14：原 AI 项目解读结果面板（aiText）已随按钮一并移除，见上方注释。 */}
        {(showBlockers || blkTitle !== null) && (
          <ProjectPausePanel api={api} id={id} zh={zh} {...pauseState} requestReason={blkTitle} setRequestReason={setBlkTitle}
            blockers={openBlockers} closeBlocker={closeBlocker} canManage={canManage}
            onChanged={async () => { await load(true); if (onChanged) await onChanged(); }} />
        )}
        {canManage && (
          <div className="mt-3 flex items-center gap-3 flex-wrap rounded-lg bg-slate-50 px-2.5 py-1.5 text-[11px]" data-testid="trace-ws-gates">
            <label className="flex items-center gap-1 cursor-pointer" data-testid="trace-ws-gate-permit">
              <input type="checkbox" checked={!!eng.permitPulled} onChange={(e) => setGate("permitPulled", e.target.checked)} className="accent-emerald-600" />
              <span className={eng.permitPulled ? "text-emerald-700 font-semibold" : "text-red-600 font-semibold"}>{zh ? "Permit 已 pulled" : "Permit pulled"}</span>
            </label>
            <label className="flex items-center gap-1 cursor-pointer" data-testid="trace-ws-gate-set1">
              <input type="checkbox" checked={!!eng.set1Complete} onChange={(e) => setGate("set1Complete", e.target.checked)} className="accent-emerald-600" />
              <span className={eng.set1Complete ? "text-emerald-700 font-semibold" : "text-red-600 font-semibold"}>{zh ? "Set 1 图纸齐全" : "Set 1 complete"}</span>
            </label>
            <span className="text-slate-300">|</span>
            <span className="text-slate-500">{zh ? "拆除判定：" : "Demo: "}
              {eng.demoNeeded === true && <span className="font-semibold text-orange-600">{zh ? "需要拆除" : "needed"}</span>}
              {eng.demoNeeded === false && <span className="font-semibold text-emerald-600">{zh ? "无需拆除（已留档）" : "not needed"}</span>}
              {eng.demoNeeded == null && (
                <>
                  <button data-testid="trace-ws-demo-yes" onClick={demoYes} className="ml-1 font-semibold text-indigo-600 hover:underline">{zh ? "需要" : "Yes"}</button>
                  <span className="text-slate-300 mx-0.5">/</span>
                  <button data-testid="trace-ws-demo-no" onClick={() => demoInput.current?.click()} className="font-semibold text-indigo-600 hover:underline">{zh ? "无需(传豁免文件)" : "No (waiver)"}</button>
                  <input ref={demoInput} type="file" className="hidden" onChange={(e) => e.target.files?.[0] && demoNoFile(e.target.files[0])} />
                </>
              )}
            </span>
          </div>
        )}
      </div>

      {/* 阶段概览默认紧凑展示，工期按需展开，把首屏空间留给任务；阶段选择和付款仍走原业务逻辑。 */}
      <TracePhaseBar key={id} phases={eng.phases || []} timings={board.timings || []} current={eng.phaseIdx} selected={selPhase} zh={zh}
        onSelect={(phase) => { setSelPhase(phase); setSelTask(null); }}
        calibration={<PhaseCalibrate api={api} id={id} zh={zh} user={user} phases={eng.phases} current={eng.phaseIdx} onDone={() => { load(); onChanged?.(); }} />}
        payPendingByPhase={payPendingByPhase}
        onOpenPayment={(payment) => nav(`/project/${id}?sub=${payment.sid}&section=payments${payment.ms ? `&ms=${payment.ms}` : ""}`)} />

      {/* 删除分类按钮后不保留空白条；仅在调用方提供额外操作时保留原插槽及 testid。 */}
      {filterBarExtra && (
        <div className="flex items-center justify-end gap-1.5" data-testid="trace-ws-filter-bar">
          {filterBarExtra}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        {/* 左：阶段任务链（双击标题 / 缩放按钮 → 全屏；点击任务 → 详情与操作直接展开在任务下方；完成后自动收起并进入下一步） */}
        <div className={full ? "fixed inset-0 z-[60] bg-white flex flex-col p-4 sm:p-6 overflow-hidden" : "rounded-2xl bg-white border border-slate-200 p-4"} data-testid="trace-ws-task-chain" data-full={full ? "1" : "0"}>
          <div className={`flex items-center justify-between gap-2 ${full ? "mobile-safe-topbar mb-3 max-w-6xl w-full mx-auto" : "mb-1"}`}>
            <p data-testid="trace-ws-chain-title" onDoubleClick={() => setFull((v) => !v)} title={zh ? "双击：全屏 / 还原" : "Double-click: fullscreen / restore"}
              className={`font-semibold text-slate-900 select-none ${full ? "text-lg cursor-zoom-out" : "text-sm cursor-zoom-in"}`}>
              {zh ? "阶段任务链" : "Task chain"} · {zh ? eng.phases?.[selPhase]?.zh : eng.phases?.[selPhase]?.en}
            </p>
            <div className="flex items-center gap-2 shrink-0">
              {!full && <p className="text-[10px] text-slate-400 hidden md:block">{zh ? "点击任务展开操作 · 完成后自动进入下一步 · 双击标题全屏" : "Click a task to act · auto-advances · double-click title for fullscreen"}</p>}
              <button data-testid="trace-ws-fullscreen-toggle" onClick={() => setFull((v) => !v)} title={full ? (zh ? "还原到板面（Esc）" : "Restore (Esc)") : (zh ? "全屏显示" : "Fullscreen")}
                className={`inline-flex items-center gap-1 rounded-lg border text-[11px] font-semibold transition-colors ${full ? "px-2.5 py-1.5 border-slate-900 bg-slate-900 text-white hover:bg-slate-700" : "p-1.5 border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                {full ? <><Minimize2 className="h-3.5 w-3.5" />{zh ? "还原" : "Restore"}</> : <Maximize2 className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
          {full && (
            <div className="flex items-center gap-1 flex-wrap max-w-6xl w-full mx-auto mb-3" data-testid="trace-ws-full-phases">
              {(eng.phases || []).map((p) => (
                <button key={p.idx} data-testid={`trace-ws-full-phase-${p.idx}`} onClick={() => { setSelPhase(p.idx); setSelTask(null); }}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${p.idx === selPhase ? "bg-brand-500 text-white border-brand-500" : p.idx === eng.phaseIdx ? "border-brand-300 text-brand-500 bg-brand-50 hover:bg-brand-100" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                  {zh ? p.zh : p.en} <span className="opacity-70">{p.done}/{p.total}</span>
                </button>
              ))}
            </div>
          )}
          {(() => {
            const counts = {};
            phaseTasks.forEach((s) => (s.tasks || []).forEach((t) => { if (t.state !== "done") counts[t.role] = (counts[t.role] || 0) + 1; }));
            const roles = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
            if (!roles.length) return null;
            return (
              <div className={`flex items-center gap-1 flex-wrap mt-2 ${full ? "max-w-6xl w-full mx-auto" : ""}`} data-testid="trace-ws-role-legend">
                <span className="text-[10px] text-slate-400 mr-0.5">{zh ? "按责任角色着色" : "Colored by owner"}</span>
                {roles.map((r) => {
                  const tint = roleTint(r); const on = roleFilter === r;
                  return (
                    <button key={r} type="button" data-testid={`trace-ws-role-legend-${r}`} data-count={counts[r]} onClick={() => setRoleFilter(on ? "" : r)} title={zh ? "点击只看该角色的待办" : "Click to focus this role"}
                      className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border transition-colors ${on ? tint.pill + " border-transparent" : `${tint.bg} ${tint.border} text-slate-700 ${tint.hover}`}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${on ? "bg-white" : tint.dot}`} />{roleName(r)} <span className="opacity-70">{counts[r]}</span>
                    </button>
                  );
                })}
                {roleFilter && <button type="button" data-testid="trace-ws-role-legend-clear" onClick={() => setRoleFilter("")} className="text-[10px] text-slate-400 hover:text-slate-700 ml-1">{zh ? "清除" : "Clear"}</button>}
              </div>
            );
          })()}
          <div className={`space-y-2.5 pr-1 mt-2 ${full ? "flex-1 overflow-y-auto max-w-6xl w-full mx-auto" : "max-h-[720px] overflow-y-auto"}`}>
            {phaseTasks.map((s) => {
              const doneTasks = (s.tasks || []).filter((t) => t.state === "done");
              const openTasks = (s.tasks || []).filter((t) => t.state !== "done");
              const showDone = !!doneOpen[s.key] || (selTask && doneTasks.some((t) => t.id === selTask.id));
              const renderTask = (t) => {
                const [bzh, ben, cls] = STATE_BADGE[t.state] || STATE_BADGE.pending;
                const active = selTask?.id === t.id;
                const links = itemsByNode[t.node] || [];
                const dim = roleFilter && t.role !== roleFilter;
                const tint = roleTint(t.role);
                return (
                  <div key={t.id} className={`mb-1 rounded-lg border transition-colors ${active ? "border-red-400 bg-white shadow-sm" : `${tint.border} ${tint.bg} ${tint.hover} border-l-[3px] ${tint.bar}`} ${dim ? "opacity-40" : ""}`} data-testid={`trace-ws-task-wrap-${t.node}`} data-active={active ? "1" : "0"} data-role={t.role || ""}>
                    <button data-testid={`trace-ws-task-${t.node}`} onClick={() => setSelTask(active ? null : t)} className="w-full text-left px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        {active ? <ChevronDown className="h-3.5 w-3.5 text-red-500 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-300 shrink-0" />}
                        <span data-testid={`trace-ws-task-title-${t.node}`} className={`text-[12px] truncate flex-1 ${["done", "skipped"].includes(t.state) ? "text-slate-400 line-through" : "text-slate-800"}`}>{zh ? t.title : (t.titleEn || T(t.title))}</span>
                        {links.slice(0, 3).map((l, i) => <span key={i} title={T(l.title)} className={`h-1.5 w-1.5 rounded-full shrink-0 ${CAT_DOT[l.cat]}`} />)}
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full shrink-0 ${cls}`}>{zh ? bzh : ben}</span>
                      </div>
                      <p className="text-[10px] text-slate-400 mt-0.5 truncate pl-5 flex items-center gap-1.5">
                        <span data-testid={`trace-ws-task-role-${t.node}`} className={`inline-flex items-center gap-1 px-1.5 py-[1px] rounded-full text-[9px] font-semibold ${tint.pill}`}>{roleName(t.role)}</span>
                        {t.requiresEvidence && <span className="text-amber-500">📎</span>}{(t.reasons || [])[0] && <span className="text-red-700 truncate">{T(t.reasons[0])}</span>}
                      </p>
                    </button>
                    {active && <div className="border-t border-red-100 px-3 py-3" data-testid="trace-ws-detail">{detailBody}</div>}
                    <TaskSubCard pid={id} node={t.node} tt={tt} zh={zh} canManage={!!tt?.canManage} onScope={setScopeTrade} onChanged={() => load(true)} />
                    {(rfq.map?.[t.node]?.trades || []).map((trade) => {
                      const entry = rfq.map[t.node];
                      const item = rfq.items.find((x) => x.node === t.node && x.trade === trade && x.status !== "cancelled") || null;
                      return <RfqCard key={trade} api={api} id={id} node={t.node} tid={entry.trades.length > 1 ? `${t.node}-${trade}` : t.node} trade={trade} when={entry.when} tradeName={item?.tradeName || rfq.items.find((x) => x.trade === trade)?.tradeName || trade} rfq={item} existingSub={tt?.subcontracts?.[trade] || null} zh={zh} canManage={!!rfq.canManage} canReview={!!rfq.canReview} onChanged={() => { loadRfq(); load(true); }} defaultOpen={active} />;
                    })}
                    {rfq.items.filter((x) => x.workNode === t.node && x.node !== t.node && x.status !== "cancelled").map((x) => (
                      <div key={x.id} data-testid={`rfq-workref-${t.node}`} className="mx-3 mb-2 rounded-lg border border-teal-200 bg-white px-3 py-1.5 text-[11px] flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-teal-800">{zh ? "分包报价" : "RFQ"} · {x.tradeName}</span>
                        <span className="text-slate-500">{zh ? `已在 ${x.node} 发起 · ` : `started at ${x.node} · `}{zh ? x.statusZh : x.status}</span>
                        {x.agreed && <span className="text-emerald-700 font-semibold">{zh ? "成交" : "Agreed"} ${Number(x.agreed.amount).toLocaleString()} · {x.invites?.find((i) => i.id === x.selectedInviteId)?.companyName || ""}{x.agreed.startDate ? ` · ${zh ? "开工" : "start"} ${fmtD(x.agreed.startDate)}` : ""}</span>}
                        {!x.agreed && <span className="text-slate-400">{x.invites?.filter((i) => i.reply).length || 0}/{x.invites?.length || 0} {zh ? "家已回复" : "replied"}</span>}
                      </div>
                    ))}
                  </div>
                );
              };
              const eta = subEta(s);
              return (
                <div key={s.key} data-testid={`trace-ws-substage-${s.key}`}>
                  <p data-testid={`trace-ws-substage-title-${s.key}`} data-eta-days={eta ?? ""} style={ink(subColor(s))}
                    className={`font-bold mb-1.5 flex items-center gap-2 leading-snug ${full ? "text-lg" : "text-base"} ${eta == null ? "text-slate-400 line-through" : ""}`}>
                    {zh ? s.name : (s.en || T(s.name))}{s.current && <span className="px-1.5 py-0.5 rounded bg-brand-500 text-white text-[9px] font-semibold no-underline">{zh ? "当前工序" : "current"}</span>}
                    <span className="text-xs font-semibold opacity-60">{s.done}/{s.total}</span>
                  </p>
                  {s.key === "01-01" && <Guide811 api={api} id={id} zh={zh} canManage={canManage || canSiteCollab} onChanged={async () => { await load(true); onChanged && onChanged(); }} />}
                  {doneTasks.length > 0 && (
                    <button data-testid={`trace-ws-done-toggle-${s.key}`} onClick={() => setDoneOpen((d) => ({ ...d, [s.key]: !showDone }))}
                      className="w-full flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50/70 border border-emerald-100 rounded-lg px-3 py-1.5 mb-1 hover:bg-emerald-50">
                      <CheckCircle2 className="h-3.5 w-3.5" />{zh ? `已完成 ${doneTasks.length} 项` : `${doneTasks.length} done`}
                      <span className="ml-auto">{showDone ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</span>
                    </button>
                  )}
                  {showDone && doneTasks.map(renderTask)}
                  {openTasks.map(renderTask)}
                </div>
              );
            })}
            {phaseTasks.length === 0 && <p className="text-xs text-slate-400">{zh ? "该阶段暂无任务卡" : "No cards"}</p>}
            {phaseTasks.length > 0 && phaseTasks.every((s) => (s.tasks || []).every((t) => t.state === "done")) && <p className="text-xs text-emerald-700 font-semibold text-center py-3" data-testid="trace-ws-phase-all-done">{zh ? "本阶段任务已全部完成 ✓" : "All done ✓"}</p>}
          </div>
        </div>

        {/* 右：项目讨论区 */}
        <div className="xl:sticky xl:top-4" data-testid="trace-ws-side">
          {sideSlot}
        </div>
      </div>

      {/* 今日必办（全宽，列表 / 任务卡 两种视图） */}
      {todaySlot}

      {/* 分类按钮已移除，保留全部业务操作卡，避免原有功能失去入口。 */}
      <div id="trace-ops-anchor" className="space-y-2">
        <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5 px-1">
          {zh ? "业务流操作面板" : "Operations"}
          <span className="text-[10px] font-normal text-slate-400">{zh ? "全部业务流" : "all"}</span>
        </p>
        <EngineOpsPanel api={api} id={id} zh={zh} user={user} onChanged={onChanged} refreshTick={refreshTick} highlight={highlight} />
      </div>

      <input ref={evInput} type="file" multiple className="hidden" data-testid="trace-ws-evidence-file" onChange={(e) => doEvidence(e.target.files)} />
      <input ref={camInput} type="file" accept="image/*" capture="environment" multiple className="hidden" data-testid="trace-ws-camera-file" onChange={(e) => doEvidence(e.target.files)} />
      <BurstCamera open={camOpen} zh={zh} onClose={() => setCamOpen(false)}
        onSubmit={(files) => { setCamOpen(false); doEvidence(files); }}
        onFallback={() => { setCamOpen(false); camInput.current?.click(); }} />
      {noteTask && <CompleteNoteModal title={noteTask.title} zh={zh} api={api}
        onConfirm={async (note, audio) => {
          await act(() => api.post(`/projects/${id}/engine/tasks/${noteTask.id}/complete`,
            { ...(note ? { note } : {}), ...(audio ? { audioFileId: audio.fileId, durationSec: audio.durationSec } : {}) }), zh ? "任务已完成 ✓" : "Done ✓");
          setNoteTask(null);
        }}
        onClose={() => setNoteTask(null)} />}
      <EvidenceLightbox ev={previewEv} zh={zh} onClose={() => setPreviewEv(null)} items={selTask?.evidence || []} onNav={setPreviewEv} />
      {scopeTrade && tt && (
        <TradeScopeDialog api={api} pid={id} trade={scopeTrade} tradeName={tt.trades?.[scopeTrade] || scopeTrade} zh={zh} overrides={tt.overrides || {}}
          allTasks={flatAll.map((t) => ({ id: t.id, node: t.node, title: t.title, titleEn: t.titleEn, sub: t.sub, subName: t.subName, phase: t.phase, trades: tt.nodeTrades?.[t.node]?.trades || [], source: tt.nodeTrades?.[t.node]?.source || "" }))}
          onClose={() => setScopeTrade(null)} onSaved={() => { loadTT(); load(true); }} />
      )}
    </div>
  );
};
